import type { ReactNode } from 'react'

import { cn } from '@/ui/lib/utils'

export type QuantMarketTone =
  | 'idle'
  | 'connecting'
  | 'warming_up'
  | 'streaming'
  | 'live'
  | 'closed'
  | 'no_feed'
  | 'reconnecting'
  | 'failed'

const TONE: Record<QuantMarketTone, { text: string; dot: string; pulse: boolean }> = {
  idle: { text: 'text-paper-40', dot: 'bg-paper-40', pulse: false },
  connecting: { text: 'text-paper-50', dot: 'bg-paper-50', pulse: false },
  streaming: { text: 'text-accent-line', dot: 'bg-accent-line', pulse: true },
  warming_up: { text: 'text-[#CB8A00]', dot: 'bg-[#CB8A00]', pulse: true },
  closed: { text: 'text-paper-50', dot: 'bg-paper-50', pulse: false },
  no_feed: { text: 'text-[#CB8A00]', dot: 'bg-[#CB8A00]', pulse: false },
  reconnecting: { text: 'text-[#CB8A00]', dot: 'bg-[#CB8A00]', pulse: true },
  failed: { text: 'text-alert', dot: 'bg-alert', pulse: false },
  live: { text: 'text-accent-line', dot: 'bg-accent-line', pulse: true },
}

export interface QuantSegmentOption<T extends string> {
  value: T
  label: string
}

export interface QuantMarketToolbarProps<TF extends string, SP extends string> {
  /** The pair dropdown. */
  pair: ReactNode
  timeframes: QuantSegmentOption<TF>[]
  timeframe: TF
  onTimeframe: (tf: TF) => void
  spans: QuantSegmentOption<SP>[]
  span: SP
  onSpan: (span: SP) => void
  /** `5m · 1,250 / 10,000`. */
  countLabel: string
  stateLabel: string
  tone: QuantMarketTone
  /** 0-100 while streaming, `null` otherwise (the 2px bar stays as an empty hairline). */
  progress: number | null
  /** Spec 070: the 1 s / 5 s tick rate. Absent hides the control. */
  rate?: { options: QuantSegmentOption<string>[]; value: string; onChange: (value: string) => void }
}

function Segment<T extends string>({
  label,
  options,
  value,
  onChange,
  padding,
}: {
  label: string
  options: QuantSegmentOption<T>[]
  value: T
  onChange: (v: T) => void
  padding: string
}) {
  return (
    <div role="group" aria-label={label} className="flex shrink-0 border border-paper-14">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          aria-pressed={opt.value === value}
          onClick={() => onChange(opt.value)}
          className={cn(
            'cursor-pointer border-0 font-mono text-[11px] tracking-[0.08em]',
            padding,
            opt.value === value ? 'bg-accent-line text-ink' : 'bg-transparent text-paper-50',
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  )
}

/**
 * Manual trade's 46px toolbar plus its 2px progress bar (handoff 4.2): pair
 * dropdown, timeframe and window segments, and on the right the count and the
 * stream state. Props only.
 */
export function QuantMarketToolbar<TF extends string, SP extends string>({
  pair,
  timeframes,
  timeframe,
  onTimeframe,
  spans,
  span,
  onSpan,
  countLabel,
  stateLabel,
  tone,
  progress,
  rate,
}: QuantMarketToolbarProps<TF, SP>) {
  const t = TONE[tone]
  return (
    <>
      <div className="flex h-[46px] shrink-0 flex-nowrap items-center gap-3.5 border-b border-paper-14 px-5">
        <div className="min-w-[130px] shrink">{pair}</div>
        <Segment label="Timeframe" options={timeframes} value={timeframe} onChange={onTimeframe} padding="px-[11px] py-1.5" />
        <Segment label="Window" options={spans} value={span} onChange={onSpan} padding="px-[9px] py-1.5" />
        {rate !== undefined && <Segment label="Update rate" options={rate.options} value={rate.value} onChange={rate.onChange} padding="px-[9px] py-1.5" />}
        <span className="min-w-0 flex-1" />
        <span
          data-quant-count
          title={countLabel}
          className="min-w-0 shrink-[20] truncate font-mono text-[10px] tracking-[0.14em] whitespace-nowrap text-paper-40 uppercase"
        >
          {countLabel}
        </span>
        <span
          role="status"
          data-quant-state={tone}
          title={stateLabel}
          className={cn(
            'flex min-w-0 shrink items-center gap-1.5 font-mono text-[10px] tracking-[0.14em] whitespace-nowrap uppercase',
            t.text,
          )}
        >
          <i
            aria-hidden="true"
            className={cn('block h-1.5 w-1.5 shrink-0', t.dot, t.pulse && 'animate-[q-pulse_1.2s_steps(1)_infinite] motion-reduce:animate-none')}
          />
          <span className="truncate">{stateLabel}</span>
        </span>
      </div>
      <div className="h-0.5 shrink-0 bg-paper-14">
        {progress !== null && (
          <div
            role="progressbar"
            aria-label="Candle stream progress"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progress}
            className="h-0.5 bg-accent-line transition-[width] duration-75 ease-linear"
            style={{ width: `${progress}%` }}
          />
        )}
      </div>
    </>
  )
}
